import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Phone, Mail, MapPin, ChevronRight, Linkedin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/logo.svg";
import FloatingActionButtons from "./FloatingActionButtons";
import CookieConsentBanner from "./CookieConsentBanner";
import LanguageSwitcher from "./LanguageSwitcher";
import { COMPANY_STATS } from "../constants/stats";
import { buildLocalizedPath, stripLangPrefix } from "../constants/languages";

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/", label: t("layout.nav.home") },
    { path: "/about", label: t("layout.nav.about") },
    { path: "/corporate-partners", label: t("layout.nav.corporatePartners") },
    { path: "/sell-panels", label: t("layout.nav.sellPanels") },
    { path: "/buy-panels", label: t("layout.nav.buyPanels") },
    { path: "/korea-operations", label: t("layout.nav.koreaOperations") },
    { path: "/compliance", label: t("layout.nav.compliance") },
    { path: "/projects", label: t("layout.nav.projects") },
    { path: "/panels", label: t("layout.nav.panels") },
    { path: "/contact", label: t("layout.nav.contact") },
  ];

  const isActive = (path) => {
    const barePath = stripLangPrefix(location.pathname);
    if (path === "/") return barePath === "/";
    return barePath.startsWith(path);
  };

  return (
    <header
      className="fixed top-0 start-0 end-0 z-50 transition-all duration-500 py-4"
      style={{ 
        backgroundColor: "rgba(11, 22, 41, 0.97)",
        backdropFilter: "blur(12px)"
      }}
    >
      {/* Main Navigation - Full Width */}
      <div className="w-full px-4">
        <nav className="flex items-center justify-between">
          {/* Logo - Left */}
          <Link to={buildLocalizedPath("/", i18n.language)} className="flex items-center" data-testid="logo-link">
            <img
              src={logo}
              alt="M A Galaxy International"
              className="h-12 w-auto"
              loading="eager"
            />
          </Link>

          {/* Right Side Group - Phone | Email | Menu */}
          <div className="flex items-center">
            {/* Phone */}
            <a 
              href="tel:+447379339449" 
              className="hidden sm:flex items-center gap-2 hover:text-gold transition-colors"
              style={{ fontSize: "13px", color: "rgba(255,255,255,0.8)" }}
            >
              <Phone size={14} className="text-gold" />
              {/* dir="ltr" keeps the digit groups in reading order under dir="rtl" ancestors (bidi reorders standalone numeric runs) */}
              <span dir="ltr">+44 7379 339 449</span>
            </a>
            
            {/* Divider */}
            <div 
              className="hidden sm:block mx-4" 
              style={{ width: "1px", height: "16px", backgroundColor: "rgba(197, 160, 89, 0.3)" }}
            />
            
            {/* Email */}
            <a 
              href="mailto:info@magalaxyintl.com" 
              className="hidden sm:flex items-center gap-2 hover:text-gold transition-colors"
              style={{ fontSize: "13px", color: "rgba(255,255,255,0.8)" }}
            >
              <Mail size={14} className="text-gold" />
              <span>info@magalaxyintl.com</span>
            </a>
            
            {/* Divider */}
            <div
              className="hidden sm:block mx-4"
              style={{ width: "1px", height: "16px", backgroundColor: "rgba(197, 160, 89, 0.3)" }}
            />

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Divider */}
            <div
              className="mx-4"
              style={{ width: "1px", height: "16px", backgroundColor: "rgba(197, 160, 89, 0.3)" }}
            />

            {/* Hamburger Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-1 ms-1 sm:ms-0"
              data-testid="menu-button"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X size={26} className="text-white" />
              ) : (
                <Menu size={26} className="text-white" />
              )}
            </button>
          </div>
        </nav>

        {/* Full Menu - All Screen Sizes */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white mt-4 rounded-sm shadow-lg overflow-hidden"
            >
              <div className="py-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={buildLocalizedPath(link.path, i18n.language)}
                    data-testid={`nav-${link.path.replace("/", "") || "home"}`}
                    className={`block px-6 py-3 text-sm font-medium uppercase tracking-wider ${
                      isActive(link.path)
                        ? "text-gold bg-slate-50"
                        : "text-navy hover:text-gold hover:bg-slate-50"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="px-6 pt-4 border-t border-slate-100 mt-4 space-y-3">
                  <a
                    href="https://www.linkedin.com/in/mushair-nawaz-726a5b8a"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="nav-linkedin-button"
                    className="flex items-center justify-center gap-2 w-full border border-gold text-gold px-6 py-3 text-xs font-bold uppercase tracking-widest rounded"
                  >
                    <Linkedin size={14} />
                    {t("layout.nav.ourLinkedin")}
                  </a>
                  <Link
                    to={buildLocalizedPath("/contact", i18n.language)}
                    data-testid="nav-cta-button"
                    className="block w-full text-center bg-gold text-navy px-6 py-3 text-xs font-bold uppercase tracking-widest"
                  >
                    {t("layout.nav.getQuote")}
                  </Link>
                </div>
                {/* Contact Info at Bottom */}
                <div className="px-6 pt-4 border-t border-slate-100 mt-4 space-y-2 text-sm text-slate-600">
                  <a href="tel:+447379339449" className="flex items-center gap-2 hover:text-gold transition-colors">
                    <Phone size={14} />
                    {/* dir="ltr" keeps the digit groups in reading order under dir="rtl" ancestors (bidi reorders standalone numeric runs) */}
                    <span dir="ltr">+44 7379 339 449</span>
                  </a>
                  <a href="mailto:info@magalaxyintl.com" className="flex items-center gap-2 hover:text-gold transition-colors">
                    <Mail size={14} />
                    info@magalaxyintl.com
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t, i18n } = useTranslation();

  return (
    // Extra bottom padding below md so the last row (copyright / compliance
    // link) always clears the fixed FloatingActionButtons stack — without it,
    // that text sat permanently behind the buttons once scrolled to the end
    // of the page, on every page, with no way to scroll further to reveal it.
    <footer className="bg-navy text-white relative noise-overlay pb-36 md:pb-0">
      <div className="container-custom">
        {/* Main Footer */}
        <div className="py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <img
              src={logo}
              alt="M A Galaxy International"
              className="h-10 w-auto mb-6"
              loading="lazy"
            />
            <p className="text-slate-400 leading-relaxed mb-6 max-w-md">
              {t("layout.footer.companyDescription")}
            </p>
            <div className="flex flex-col gap-3 text-sm">
              <a href="tel:+447379339449" className="text-slate-400 hover:text-gold transition-colors flex items-center gap-2">
                <Phone size={16} />
                +44 7379 339 449
              </a>
              <a href="mailto:info@magalaxyintl.com" className="text-slate-400 hover:text-gold transition-colors flex items-center gap-2">
                <Mail size={16} />
                info@magalaxyintl.com
              </a>
            </div>
          </div>

          {/* UK Office */}
          <div>
            <h4 className="font-outfit text-lg font-semibold mb-6 text-gold">{t("layout.footer.ukOfficeLabel")}</h4>
            <div className="flex items-start gap-2 text-slate-400 text-sm">
              <MapPin size={16} className="mt-1 flex-shrink-0" />
              <address className="not-italic leading-relaxed">
                62 New Road, Newhall<br />
                Swadlincote, Derby<br />
                DE11 0TH, United Kingdom
              </address>
            </div>
            <div className="mt-4 text-xs text-slate-500">
              <p>{t("layout.footer.companyRegLabel")} 13671542</p>
              <p>{t("layout.footer.vatLabel")} GB425010549</p>
              <p>{t("layout.footer.eoriLabel")} GB425010549000</p>
            </div>
          </div>

          {/* Korea HQ */}
          <div>
            <h4 className="font-outfit text-lg font-semibold mb-6 text-gold">{t("layout.footer.koreaHqLabel")}</h4>
            <div className="flex items-start gap-2 text-slate-400 text-sm">
              <MapPin size={16} className="mt-1 flex-shrink-0" />
              <address className="not-italic leading-relaxed">
                4385-23 Seodong-daero<br />
                Gongdo-eup, Anseong<br />
                Gyeonggi-do, South Korea
              </address>
            </div>
            <div className="mt-4 text-xs text-slate-500">
              <p>{t("layout.footer.foundedLabel")} 2000</p>
              <p>{COMPANY_STATS.yearsExperience}+ {t("layout.footer.yearsExperienceLabel")}</p>
              <p className="mt-1 text-slate-600">{t("layout.footer.ukEntityEstablished")}</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            {t("layout.footer.copyright", { year: currentYear })}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 text-sm">
            <a
              href="https://www.linkedin.com/in/mushair-nawaz-726a5b8a"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-gold transition-colors"
              data-testid="footer-linkedin"
            >
              <Linkedin size={16} />
              {t("layout.footer.followLinkedin")}
            </a>
            <span className="text-slate-400">{t("layout.footer.wasteCarrierLicenceLabel")} CBDU628298</span>
            <Link to={buildLocalizedPath("/compliance", i18n.language)} className="text-slate-400 hover:text-gold transition-colors">
              {t("layout.footer.complianceLink")}
            </Link>
            <Link to={buildLocalizedPath("/privacy", i18n.language)} className="text-slate-400 hover:text-gold transition-colors">
              {t("layout.footer.privacyLink")}
            </Link>
            <Link to={buildLocalizedPath("/terms", i18n.language)} className="text-slate-400 hover:text-gold transition-colors">
              {t("layout.footer.termsLink")}
            </Link>
            {/* Reopens CookieConsentBanner so a visitor can change an earlier choice — this is the mechanism the Privacy Policy promises exists */}
            <button
              onClick={() => window.dispatchEvent(new Event("ma-reopen-cookie-settings"))}
              className="text-slate-400 hover:text-gold transition-colors"
              data-testid="footer-cookie-settings"
            >
              {t("layout.footer.cookieSettingsLink")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Layout = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <FloatingActionButtons />
      <CookieConsentBanner />
    </div>
  );
};

export default Layout;
